import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { SortType } from "../../../../models/SortType";
import {
  Box,
  CircularProgress,
  Container,
  Paper,
  Backdrop,
  Grid2,
  Collapse,
  IconButton,
  useTheme,
} from "@mui/material";
import {
  Download as DownloadIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import CustomTypography from "../../../components/common/form/CustomTypography";
import AppButton from "../../../components/common/buttons/AppButton";
import CustomTextField from "../../../components/common/textFields/TextField";
import { useStores } from "../../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotification } from "../../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../../components/common/pop-up/PermissionBlockLabel ";
import SwageazyInventoryProductsTable from "../../../components/swageazy-inventory/SwageazyInventoryProductsTable";
import { SwageazyInventoryFilters } from "../../../../models/SwageazyInventory/SwageazyInventoryFilter";
import AppDropDown from "../../../components/common/filter/AppDropDown";
import {
  QuantityDetails,
  SwageazyInventoryDetails,
  SwageazyInventoryProductRequestVariant,
} from "../../../../models/SwageazyInventory/SwageazyInventory";
import ImageGalleryPopup from "../../../components/common/pop-up/ImageGalleryPopup";
import SwageazyInventoryInwardPopup from "./SwageazyInventoryInwardPopup";
import SwageazyInventoryOutwardPopup from "./SwageazyInventoryOutwardPopup";
import SwageazyInventorySampleReturn from "./SwageazyInventorySampleReturn";
import SwageazyInventoryStockQuantityDetailsPopup from "./SwageazyInventoryStockQyantityDetailsPopup";
import CreatePurchaseRequestPopup from "../sourcing-ops/CreatePrPopup";
import dayjs from "dayjs";
import { PurchaseRequestProductRequestBody } from "../../../../models/PurchaseRequest/PurchaseRequestProductRequestBody";
import { useDebounce } from "../../../components/common/hooks/Debounce";

const SwageazyInventoryListing: React.FC = () => {
  const { userStore, swageazyInventoryStore, warehouseStore } = useStores();
  const theme = useTheme();
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const parseUrlParams = (): SwageazyInventoryFilters => {
    return {
      page_no: parseInt(searchParams.get("page_no") || "1", 10),
      search_text: searchParams.get("search_text") || undefined,
      warehouse_id: searchParams.get("warehouse_id") || "",
      limit: parseInt(searchParams.get("limit") || "30", 10),
      sort_by: searchParams.get("sort_by") || "last_updated_at",
      sort_type:
        searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
    };
  };

  const initialParams: SwageazyInventoryFilters = parseUrlParams();
  const [params, setParams] = useState<SwageazyInventoryFilters>(initialParams);
  const [isSearchExpanded, setIsSearchExpanded] = useState(true);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || "",
  );
  const [draftParams, setDraftParams] = useState<SwageazyInventoryFilters>({
    warehouse_id: initialParams.warehouse_id || "",
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 1000);

  useEffect(() => {
    const trimmed = debouncedSearchTerm.trim();
    // Avoid triggering searches for very short input.
    const newSearch = trimmed.length >= 3 ? trimmed : undefined;

    setParams((prev: SwageazyInventoryFilters) => {
      const prevSearch = prev.search_text ?? "";
      const nextSearch = newSearch ?? "";

      if (prevSearch === nextSearch) return prev;

      return {
        ...prev,
        search_text: newSearch,
        page_no: 1,
      };
    });
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (userStore.hasPermission([EmployeeRoles.VIEW_SWAGEAZY_INVENTORY])) {
      swageazyInventoryStore.isLoading = true;
      swageazyInventoryStore
        .getSwageazyInventoryListing(params)
        .then(() => {})
        .catch((err: any) => {
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          );
        })
        .finally(() => {
          swageazyInventoryStore.isLoading = false;
          swageazyInventoryStore.isFetchingData = false;
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (!userStore.hasPermission([EmployeeRoles.VIEW_SWAGEAZY_INVENTORY]))
      return;
    if (warehouseStore.warehouses.length > 0) return;
    warehouseStore.searchWarehouse().catch(() => {
      showError("Error", "Failed to fetch warehouses.");
    });
  }, [userStore, warehouseStore, showError]);

  const handleSearch = (query: string) => {
    const trimmed = query.trim();
    setSearchTerm(query);
    setParams((prev: SwageazyInventoryFilters) => ({
      ...prev,
      warehouse_id: draftParams.warehouse_id || "",
      search_text: trimmed.length >= 3 ? trimmed : undefined,
      page_no: 1,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      const trimmedSearchTerm = searchTerm.trim();
      if (trimmedSearchTerm.length > 2) {
        setParams((prev: SwageazyInventoryFilters) => ({
          ...prev,
          warehouse_id: draftParams.warehouse_id || "",
          search_text: trimmedSearchTerm,
          page_no: 1,
        }));
      } else {
        setSearchTerm("");
        setParams((prev: SwageazyInventoryFilters) => ({
          ...prev,
          warehouse_id: draftParams.warehouse_id || "",
          search_text: undefined,
          page_no: 1,
        }));
      }
    }
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setParams((prev: SwageazyInventoryFilters) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev: SwageazyInventoryFilters) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const resetFilters = () => {
    setSearchTerm("");
    setDraftParams((prev) => ({ ...prev, warehouse_id: "" }));
    setParams((prev: SwageazyInventoryFilters) => ({
      ...prev,
      search_text: undefined,
      warehouse_id: "",
      page_no: 1,
    }));
  };

  const handleApply = () => {
    const trimmed = searchTerm.trim();
    setParams((prev: SwageazyInventoryFilters) => ({
      ...prev,
      warehouse_id: draftParams.warehouse_id || "",
      search_text: trimmed.length >= 3 ? trimmed : undefined,
      page_no: 1,
    }));
  };

  useEffect(() => {
    const updateQueryParams = () => {
      const queryParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          if (
            typeof value === "string" ||
            typeof value === "number" ||
            typeof value === "boolean"
          ) {
            queryParams.append(key, String(value));
          }
        }
      });

      const queryString = queryParams.toString();
      if (queryString) {
        navigate({ search: `?${queryString}` }, { replace: true });
      } else {
        navigate({ search: "" }, { replace: true });
      }
    };
    updateQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, navigate]);

  const handleViewImagesOfProduct = (product: SwageazyInventoryDetails) => {
    swageazyInventoryStore.setSelectedProductImages(
      product?.id,
      product?.images || [],
    );
    swageazyInventoryStore.managePopup("openImageGallery", "open");
  };

  const handleViewInwardQuantityDetails = (id: number) => {
    swageazyInventoryStore.selectedProduct = id;
    swageazyInventoryStore.managePopup(
      "InventoryStockQuantityDetailsPopup",
      "open",
    );
    swageazyInventoryStore.getProductStockDetails(id).catch((err: any) => {
      showError(
        "Error",
        err?.response?.data?.message ||
          "Something went wrong! Please try again later.",
      );
    });
  };

  const handlePr = () => {
    if (swageazyInventoryStore.isLoading) {
      return;
    }

    swageazyInventoryStore.setIsLoading(true);

    const vendorId = Array.isArray(
      swageazyInventoryStore.purchaseRequestFormData?.vendor_id,
    )
      ? Number(
          swageazyInventoryStore.purchaseRequestFormData?.vendor_id?.[0]?.id,
        )
      : Number(swageazyInventoryStore.purchaseRequestFormData?.vendor_id);

    if (!vendorId) {
      swageazyInventoryStore.setIsLoading(false);
      showError("Error occurred", "Please select vendor");
      return;
    }

    if (
      vendorId === Number(process.env.REACT_APP_VENDOR_ID) &&
      !swageazyInventoryStore.purchaseRequestFormData?.warehouse_id
    ) {
      swageazyInventoryStore.setIsLoading(false);
      showError("Error occurred", "Please select city and warehouse");
      return;
    }

    if (
      !swageazyInventoryStore.purchaseRequestFormData?.expected_delivery_date
    ) {
      swageazyInventoryStore.setIsLoading(false);
      showError("Error occurred", "Please select expected delivery date");
      return;
    }

    const products: PurchaseRequestProductRequestBody[] = [];

    swageazyInventoryStore.purchaseRequestProducts?.forEach((prProduct) => {
      const product = swageazyInventoryStore.products?.find(
        (p) => p.id === prProduct.id,
      );
      if (!product) return;

      if (prProduct.quantity && prProduct.cost_price) {
        products.push({
          id: prProduct.id,
          quantity: prProduct.quantity,
          rate: prProduct.rate || prProduct.cost_price,
          sku: product.sku || undefined,
          description:
            prProduct.description || product.description || undefined,
          gst_percentage: product.gst_percentage || prProduct.gst_percentage,
        });
      }
    });

    // COMMENTED OUT: Old variant-based processing
    // swageazyInventoryStore.selectedProductVariants.forEach(
    //   (variants, productId) => {
    //     const product = swageazyInventoryStore.products?.find(
    //       (p) => p.id === productId
    //     );
    //     if (!product) return;

    //     variants?.forEach((variant) => {
    //       if (
    //         variant?.quantity &&
    //         variant?.costPrice &&
    //         variant?.quantity > 0 &&
    //         variant?.costPrice > 0
    //       ) {
    //         const prProduct =
    //           swageazyInventoryStore.purchaseRequestProducts?.find(
    //             (p) => p?.id === productId
    //           );

    //         const sizeValue = variant?.selectedOptions?.["1"] || "";
    //         const colorValue = variant?.selectedOptions?.["2"] || "";

    //         let sku = "";
    //         if (sizeValue && colorValue) {
    //           sku = `${sizeValue}-${colorValue}`.toLowerCase();
    //         } else if (sizeValue) {
    //           sku = sizeValue.toLowerCase();
    //         } else if (colorValue) {
    //           sku = colorValue.toLowerCase();
    //         }

    //         products.push({
    //           id: productId,
    //           quantity: variant?.quantity,
    //           rate: variant?.costPrice,
    //           sku: sku || undefined,
    //           description:
    //             prProduct?.description || product?.description || undefined,
    //         });
    //       }
    //     });
    //   }
    // );

    if (products.length === 0) {
      swageazyInventoryStore.setIsLoading(false);
      showError(
        "Error occurred",
        "Please add at least one product with quantity and cost price",
      );
      return;
    }

    const expectedDeliveryDate =
      swageazyInventoryStore.purchaseRequestFormData?.expected_delivery_date;
    const expectedDeliveryDateString =
      expectedDeliveryDate instanceof Date
        ? dayjs(expectedDeliveryDate).format("YYYY-MM-DD HH:mm:ss")
        : typeof expectedDeliveryDate === "string"
          ? dayjs(expectedDeliveryDate).format("YYYY-MM-DD HH:mm:ss")
          : dayjs().format("YYYY-MM-DD HH:mm:ss");

    swageazyInventoryStore.setPurchaseRequestFormData({
      emails: swageazyInventoryStore.purchaseRequestFormData?.emails || [],
      vendor_id: vendorId,
      vendor_name: swageazyInventoryStore.purchaseRequestFormData?.vendor_name,
      warehouse_id:
        swageazyInventoryStore.purchaseRequestFormData?.warehouse_id,
      expected_delivery_date: expectedDeliveryDateString,
      is_urgent_sourcing_request:
        swageazyInventoryStore.purchaseRequestFormData
          ?.is_urgent_sourcing_request || false,
      pr_products: products,
      vendor_email:
        swageazyInventoryStore.purchaseRequestFormData?.vendor_email,
    });

    swageazyInventoryStore
      .createSwageazyInventoryPurchaseRequest()
      .then(() => {
        showSuccess("Purchase Request Created");
        swageazyInventoryStore.resetCreatePurchaseRequestState();
        swageazyInventoryStore.managePopup("openCreatePrPopup", "close");
      })
      .catch((err: any) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => {
        swageazyInventoryStore.setIsLoading(false);
      });
  };

  const handleInwardTransfer = () => {
    swageazyInventoryStore
      .createSwageazyInventoryInwardTransfer()
      .then(() => {
        swageazyInventoryStore.setInwardRequestFormData({
          box_id: undefined,
        });
        showSuccess("Inward Transferred Successfully");
        swageazyInventoryStore.managePopup("openInwardPopup", "close");
        swageazyInventoryStore.getSwageazyInventoryListing(params);
      })
      .catch((err: any) => {
        swageazyInventoryStore.setInwardRequestFormData({
          box_id: undefined,
        });
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      });
  };

  const handleOutwardTransfer = () => {
    const quantityDetails: QuantityDetails[] = [];

    let totalQuantity = 0;

    if (
      !swageazyInventoryStore.outwardRequestFormData?.po_id ||
      !swageazyInventoryStore.outwardRequestFormData?.product_id
    ) {
      showError("Validation Error", "Product Id missing");
      return;
    }

    if (
      !swageazyInventoryStore.outwardRequestFormData?.warehouse_id ||
      swageazyInventoryStore.outwardRequestFormData?.warehouse_id === "-1"
    ) {
      showError("Validation Error", "Warehouse missing");
      return;
    }

    if (
      swageazyInventoryStore.outwardRequestFormData?.boxes &&
      swageazyInventoryStore.outwardRequestFormData?.boxes?.length > 0
    ) {
      swageazyInventoryStore.outwardRequestFormData?.boxes?.forEach((box) => {
        const boxId =
          box?.boxes && box?.boxes?.length > 0 ? box?.boxes?.[0] : undefined;
        const quantity = box?.quantity || box?.stock?.quantity || 0;
        const sku = box?.sku || "";

        if (boxId && quantity > 0 && sku && sku?.length > 0) {
          quantityDetails.push({
            sku: sku,
            box_id: boxId,
            quantity: quantity,
          });
          totalQuantity += quantity;
        }
      });
    }

    if (quantityDetails.length === 0) {
      showError("Validation Error", "No products selected");
      return;
    }

    let availableQuantity = 0;
    if (
      swageazyInventoryStore.productStockDetails?.blocked_stock &&
      swageazyInventoryStore.outwardRequestFormData?.po_id &&
      swageazyInventoryStore.outwardRequestFormData?.product_id &&
      swageazyInventoryStore.outwardRequestFormData?.warehouse_id
    ) {
      const blockedStocks =
        swageazyInventoryStore.productStockDetails?.blocked_stock?.filter(
          (stock) =>
            stock?.product_id ===
              swageazyInventoryStore.outwardRequestFormData?.product_id &&
            String(stock?.warehouse) ===
              String(
                swageazyInventoryStore.outwardRequestFormData?.warehouse_id,
              ),
        ) || [];
      availableQuantity = blockedStocks.reduce((sum, stock) => {
        return sum + (parseInt(stock?.quantity) || 0);
      }, 0);
    }

    if (totalQuantity > availableQuantity) {
      showError("Validation Error", "Quantity exceeds available stock");
      return;
    }

    const requestData = {
      ...swageazyInventoryStore.outwardRequestFormData,
      quantity_details: quantityDetails,
    };

    swageazyInventoryStore.setOutwardRequestFormData(requestData);

    swageazyInventoryStore
      .createSwageazyInventoryOutwardTransfer()
      .then(() => {
        const totalQuantity = quantityDetails.reduce(
          (sum, detail) => sum + (detail.quantity || 0),
          0,
        );

        const productId =
          swageazyInventoryStore.outwardRequestFormData?.product_id;
        const currentUserName = userStore.user?.name || "System";

        if (productId) {
          swageazyInventoryStore.updateInventoryListingAfterOutward(
            productId,
            totalQuantity,
            currentUserName,
          );
        }

        showSuccess("Outward Transfer Created");
        swageazyInventoryStore.managePopup("openOutwardPopup", "close");
      })
      .catch((err: any) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => {
        swageazyInventoryStore.setIsLoading(false);
      });
  };

  const handleSampleReturn = () => {
    if (
      !swageazyInventoryStore.sampleReturnRequestFormData?.variants ||
      swageazyInventoryStore.sampleReturnRequestFormData?.variants.length ===
        0 ||
      !swageazyInventoryStore.sampleReturnRequestFormData?.variants?.some(
        (variant: SwageazyInventoryProductRequestVariant) =>
          variant?.quantity !== undefined &&
          variant?.quantity !== null &&
          variant?.quantity > 0,
      )
    ) {
      showError(
        "Validation Error",
        "Please add at least one valid variant with quantity rejected.",
      );
      return;
    }

    if (!swageazyInventoryStore.sampleReturnRequestFormData?.order_id) {
      showError("Validation Error", "Please select a valid Order ID.");
      return;
    }

    if (!swageazyInventoryStore.sampleReturnRequestFormData.product_id) {
      showError("Validation Error", "Please select a valid Product.");
      return;
    }

    if (
      !swageazyInventoryStore.sampleReturnRequestFormData.box_id ||
      swageazyInventoryStore.sampleReturnRequestFormData.box_id === undefined
    ) {
      showError("Validation Error", "Please enter a valid Box ID.");
      return;
    }

    if (!swageazyInventoryStore.sampleReturnRequestFormData.current_rack) {
      showError("Validation Error", "Please select a valid Rack.");
      return;
    }

    swageazyInventoryStore.setIsLoading(true);
    swageazyInventoryStore
      .sampleReturn()
      .then(() => {
        // Calculate total quantity from all variants
        const totalQuantity =
          swageazyInventoryStore.sampleReturnRequestFormData?.variants?.reduce(
            (sum, variant) => sum + (variant.quantity || 0),
            0,
          ) || 0;

        // Get product ID and current user
        const productId =
          swageazyInventoryStore.sampleReturnRequestFormData?.product_id;
        const currentUserName = userStore.user?.name || "System";

        // Update inventory listing
        if (productId) {
          swageazyInventoryStore.updateInventoryListingAfterSampleReturn(
            productId,
            totalQuantity,
            currentUserName,
          );
        }

        showSuccess("Sample Returned Successfully");
        swageazyInventoryStore.managePopup("openSampleReturnPopup", "close");
      })
      .catch((err: any) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => {
        swageazyInventoryStore.setIsLoading(false);
      });
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ px: 2 }}>
      <Backdrop
        sx={(theme) => ({
          color: theme.palette.customColors.white,
          zIndex: theme.zIndex.drawer + 999999,
        })}
        open={swageazyInventoryStore.isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Paper>
        {userStore.hasPermission(EmployeeRoles.VIEW_SWAGEAZY_INVENTORY) ? (
          <Box
            sx={{
              marginTop: "0.6rem",
              pt: 5,
              pl: 5,
              pr: 5,
              pb: 1,
              boxShadow:
                "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
              borderRadius: "4px",
              minHeight: window.innerHeight - 110,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
                alignItems: "center",
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <CustomTypography variant="heading3" fontWeight={700}>
                  Swageazy Inventory
                </CustomTypography>
              </Box>
              {userStore.hasPermission([
                EmployeeRoles.EDIT_SWAGEAZY_INVENTORY,
              ]) && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    marginLeft: "auto",
                    flexWrap: "wrap",
                  }}
                >
                  <AppButton
                    variant="secondary"
                    label="Create PR"
                    onClick={() =>
                      swageazyInventoryStore.managePopup(
                        "openCreatePrPopup",
                        "open",
                      )
                    }
                  />
                  <AppButton
                    variant="grayscale"
                    label="Inward"
                    onClick={() =>
                      swageazyInventoryStore.managePopup(
                        "openInwardPopup",
                        "open",
                      )
                    }
                  />
                  <AppButton
                    variant="grayscale"
                    label="Outward"
                    onClick={() =>
                      swageazyInventoryStore.managePopup(
                        "openOutwardPopup",
                        "open",
                      )
                    }
                  />
                  <AppButton
                    variant="grayscale"
                    label="Sample Return"
                    onClick={() =>
                      swageazyInventoryStore.managePopup(
                        "openSampleReturnPopup",
                        "open",
                      )
                    }
                  />
                  <AppButton
                    startIcon={<DownloadIcon />}
                    variant="primary"
                    label="Download Data"
                    onClick={() =>
                      swageazyInventoryStore
                        .downloadSwageazyInventoryReport(params)
                        .then((response) => {
                          if (response.data) {
                            const downloadUrl = window.URL.createObjectURL(
                              response.data,
                            );
                            const a = document.createElement("a");
                            a.href = downloadUrl;
                            a.download =
                              response.filename || "swageazy-inventory.csv";
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            window.URL.revokeObjectURL(downloadUrl);
                          }
                        })
                        .catch((err: any) => {
                          showError(
                            "Error",
                            err?.response?.data?.message ||
                              "Something went wrong! Please try again later.",
                          );
                        })
                    }
                  />
                </Box>
              )}
            </Box>

            <Paper
              variant="outlined"
              sx={{
                mt: 2,
                borderRadius: 2,
                borderColor: "divider",
                overflow: "hidden",
              }}
            >
              <Box
                onClick={() => setIsSearchExpanded(!isSearchExpanded)}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 0.5,
                  cursor: "pointer",
                  borderBottom: isSearchExpanded ? "1px solid" : "none",
                  borderColor: "divider",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <CustomTypography variant="heading6">Search</CustomTypography>
                <IconButton size="small">
                  {isSearchExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={isSearchExpanded} timeout="auto" unmountOnExit>
                <Grid2 container spacing={2} columns={12} padding={2}>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                    <CustomTextField
                      placeholder={"Search by Product Name "}
                      value={searchTerm}
                      // onSearch={(query) => handleSearch(query)}
                      onKeyDown={handleKeyDown}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      width={"100%"}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <AppDropDown
                      title="Warehouse"
                      placeholderText="All Warehouses"
                      isMultiSelect={false}
                      value={draftParams.warehouse_id || ""}
                      list={[
                        { id: "", value: "All Warehouses" },
                        ...warehouseStore.warehouses.map((warehouse) => ({
                          id: warehouse.id,
                          value: warehouse.identifier,
                        })),
                      ]}
                      fontSize={"12px"}
                      onSingleSelectionChange={(id) => {
                        setDraftParams((prev) => ({ ...prev, warehouse_id: id }));
                      }}
                      sx={{ width: "100%" }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}></Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}></Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <AppButton
                        variant="transparentPrimary"
                        label="Clear"
                        onClick={resetFilters}
                        customStyles={{ width: "100%" }}
                      />
                      <AppButton
                        variant="primary"
                        label="Apply"
                        onClick={handleApply}
                        customStyles={{ width: "100%" }}
                      />
                    </Box>
                  </Grid2>
                </Grid2>
              </Collapse>
            </Paper>

            {swageazyInventoryStore.isFetchingData ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: window.innerHeight - 340,
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <SwageazyInventoryProductsTable
                handleViewImages={handleViewImagesOfProduct}
                maxHeight={window.innerHeight - 340}
                limit={params.limit || swageazyInventoryStore.limit}
                page_no={params.page_no || 1}
                total_count={swageazyInventoryStore.total_count}
                total_pages={swageazyInventoryStore.total_pages}
                sortBy={params.sort_by || "name"}
                sortType={params.sort_type as SortType}
                onSortChange={(column, sortType) => {
                  setParams((prev) => ({
                    ...prev,
                    sort_by: column,
                    sort_type: sortType,
                  }));
                }}
                data={swageazyInventoryStore.swageazyInventoryListingResponse}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                handleQuantityClick={(id: number) =>
                  handleViewInwardQuantityDetails(id)
                }
              />
            )}
          </Box>
        ) : (
          <PermissionBlockLabel />
        )}
      </Paper>
      <ImageGalleryPopup
        key={"productImages"}
        open={swageazyInventoryStore.popupState.openImageGallery}
        onClose={() =>
          swageazyInventoryStore.managePopup("openImageGallery", "close")
        }
        images={
          swageazyInventoryStore.selectedProductImages?.length > 0
            ? swageazyInventoryStore.selectedProductImages?.map((img) => ({
                id: swageazyInventoryStore.selectedProduct || 0,
                src: img,
              })) || []
            : []
        }
        title={"Product Images"}
      />

      <CreatePurchaseRequestPopup
        open={swageazyInventoryStore.popupState.openCreatePrPopup}
        orderId={-1}
        onClose={() =>
          swageazyInventoryStore.managePopup("openCreatePrPopup", "close")
        }
        showSummary={false}
        showGmailRecipients={true}
        enableProductSearch={true}
        handlePr={handlePr}
      />

      <SwageazyInventoryInwardPopup
        open={swageazyInventoryStore.popupState.openInwardPopup}
        onClose={() =>
          swageazyInventoryStore.managePopup("openInwardPopup", "close")
        }
        onSave={handleInwardTransfer}
      />
      <SwageazyInventoryOutwardPopup
        open={swageazyInventoryStore.popupState.openOutwardPopup}
        onClose={() =>
          swageazyInventoryStore.managePopup("openOutwardPopup", "close")
        }
        onSave={handleOutwardTransfer}
      />

      <SwageazyInventorySampleReturn
        open={swageazyInventoryStore.popupState.openSampleReturnPopup}
        onClose={() =>
          swageazyInventoryStore.managePopup("openSampleReturnPopup", "close")
        }
        onSave={handleSampleReturn}
      />

      <SwageazyInventoryStockQuantityDetailsPopup
        open={
          swageazyInventoryStore.popupState.InventoryStockQuantityDetailsPopup
        }
        onClose={() =>
          swageazyInventoryStore.managePopup(
            "InventoryStockQuantityDetailsPopup",
            "close",
          )
        }
      />
    </Grid2>
  );
};
export default observer(SwageazyInventoryListing);
